import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import Head from "next/head";
import styles from "../styles/layout.standard.module.css";
import Link from "next/link";
import Image from "next/image";
import SearchBar from "./search-bar";
import { AppSettings } from "../lib/get-app-settings";
import isDarkColor from "../lib/is-dark-color";
import AtlasSnippet from "./atlas-snippet";
import getBaseUrl from "../lib/get-base-url";
import { useRouter } from "next/router";
// import UserAvatar from "../components/user-avatar";
// import Customer from "../models/customer";
import t from "../texts/t";
import { HelpCenterTheme } from "../lib/load-company-settings";
import { createSchemeCss } from "@atlas/tool/box/style/createSchemeStyleSheet";
import usePrefersColorScheme from "@atlas/tool/box/hooks/usePrefersColorScheme";
import LayoutFooter from "./layout-footer";
import useUrlWithPreview from "../lib/use-url-with-preview";
import imageDomainsWhitelist from "../lib/image-domains-whitelist.json";

type TStandardLayoutProps = {
  className?: string;
  pageTitle?: string;
  isCustomPageTitle?: boolean;
  articleLocale?: string | null;
  appSettings: AppSettings;
  pageDescription?: string;
  children: React.ReactNode;
  searchValue?: string;
  hasTitle?: boolean;
  canonicalUrl?: string | null;
  seoImageUrl?: string;
  hideSearch?: boolean;
  hideFooter?: boolean;
  header?: React.ReactNode;
  top?: React.ReactNode;
  sidebar?: React.ReactNode;
  // customer?: Customer | null;
  noContentTopPadding?: boolean;
};

const customerPortalPath = "/customer-portal";

export default function StandardLayout({
  className,
  pageTitle,
  isCustomPageTitle,
  articleLocale,
  appSettings,
  pageDescription,
  children,
  searchValue,
  hasTitle,
  canonicalUrl,
  seoImageUrl,
  hideSearch = false,
  hideFooter = false,
  header,
  top,
  sidebar,
  // customer,
  noContentTopPadding,
}: TStandardLayoutProps) {
  const hcSuffix = appSettings.title;

  const prefersColorScheme = usePrefersColorScheme();

  const ssrLogoUrl = appSettings.logoUrl || appSettings.logoDarkUrl;
  const logoUrl = useMemo<string | undefined>(() => {
    if (appSettings.theme === HelpCenterTheme.LIGHT) return appSettings.logoUrl;
    if (appSettings.theme === HelpCenterTheme.DARK)
      return appSettings.logoDarkUrl;
    return prefersColorScheme === "light"
      ? appSettings.logoUrl
      : appSettings.logoDarkUrl;
  }, [
    prefersColorScheme,
    appSettings.theme,
    appSettings.logoUrl,
    appSettings.logoDarkUrl,
  ]);

  const [wallpaperUrl, setWallpaperUrl] = useState<string>();

  useEffect(
    () =>
      setWallpaperUrl(() => {
        if (appSettings.theme === HelpCenterTheme.LIGHT)
          return appSettings.wallpaperLight;
        if (appSettings.theme === HelpCenterTheme.DARK)
          return appSettings.wallpaperDark;
        return prefersColorScheme === "light"
          ? appSettings.wallpaperLight
          : appSettings.wallpaperDark;
      }),
    [
      prefersColorScheme,
      appSettings.theme,
      appSettings.wallpaperLight,
      appSettings.wallpaperDark,
    ]
  );

  pageDescription = pageDescription
    ? pageDescription
    : hcSuffix
    ? t("Knowledge base of {0}", appSettings.language, [hcSuffix])
    : t("Knowledge base", appSettings.language);

  // TODO: It seems this calls extra hooks
  const colorsCss = useMemo(() => {
    const light = (opacity = 1) => `rgba(255, 255, 255, ${opacity})`;
    const dark = (opacity = 1) => `rgba(0, 0, 0, ${opacity})`;

    const isPrimaryLightIsActuallyDark = isDarkColor(
      appSettings.primaryColorLight
    );
    const isSecondaryLightIsActuallyDark = isDarkColor(
      appSettings.secondaryColorLight
    );
    const isPrimaryDarkIsActuallyDark = isDarkColor(
      appSettings.primaryColorDark
    );
    const isSecondaryDarkIsActuallyDark = isDarkColor(
      appSettings.secondaryColorDark
    );

    const primaryInvertedColorLight = isPrimaryLightIsActuallyDark
      ? light
      : dark;
    const secondaryInvertedColorLight = isSecondaryLightIsActuallyDark
      ? light
      : dark;
    const primaryInvertedColorDark = isPrimaryDarkIsActuallyDark ? light : dark;
    const secondaryInvertedColorDark = isSecondaryDarkIsActuallyDark
      ? light
      : dark;

    const linkColorLight = isSecondaryLightIsActuallyDark
      ? "#6666ff"
      : "#0000ff";
    const linkColorDark = isSecondaryDarkIsActuallyDark ? "#6666ff" : "#0000ff";
    const highlightColorLight = isSecondaryLightIsActuallyDark
      ? "#ffffff"
      : "#262626";
    const highlightColorDark = isSecondaryDarkIsActuallyDark
      ? "#ffffff"
      : "#262626";

    const lightTile = {
      background: "#ffffff",
      border: "#ece8db",
      icon: "#fbf9f6",
      title: "#242424",
      text: "#747474",
      hint: "#fbf9f6",
    };

    const darkTile = {
      background: "#222222",
      border: "#000000",
      icon: "#333333",
      title: "#ffffff",
      text: "#9c9c9c",
      hint: "#313435",
    };

    const searchLightIcon = "#a4a4a4";
    const searchDarkIcon = "#ffffff";

    return createSchemeCss(
      {
        primary: [appSettings.primaryColorLight, appSettings.primaryColorDark],
        primaryInverted: [
          primaryInvertedColorLight(),
          primaryInvertedColorDark(),
        ],
        primaryInverted95: [
          primaryInvertedColorLight(0.95),
          primaryInvertedColorDark(0.95),
        ],
        primaryInverted55: [
          primaryInvertedColorLight(0.55),
          primaryInvertedColorDark(0.55),
        ],
        primaryInverted50: [
          primaryInvertedColorLight(0.5),
          primaryInvertedColorDark(0.5),
        ],
        primaryInverted24: [
          primaryInvertedColorLight(0.24),
          primaryInvertedColorDark(0.24),
        ],
        secondary: [
          appSettings.secondaryColorLight,
          appSettings.secondaryColorDark,
        ],
        secondaryInverted: [
          secondaryInvertedColorLight(),
          secondaryInvertedColorDark(),
        ],
        secondaryInverted85: [
          secondaryInvertedColorLight(0.85),
          secondaryInvertedColorDark(0.85),
        ],
        secondaryInverted55: [
          secondaryInvertedColorLight(0.55),
          secondaryInvertedColorDark(0.55),
        ],
        secondaryInverted30: [
          secondaryInvertedColorLight(0.3),
          secondaryInvertedColorDark(0.3),
        ],
        tile: [
          isSecondaryLightIsActuallyDark ? darkTile.background : "transparent",
          isSecondaryDarkIsActuallyDark ? darkTile.background : "transparent",
        ],
        filledTile: [
          isSecondaryLightIsActuallyDark
            ? darkTile.background
            : lightTile.background,
          isSecondaryDarkIsActuallyDark
            ? darkTile.background
            : lightTile.background,
        ],
        tileMarker: ["#fbf9f6", "#fbf9f6"],
        tileIcon: [
          isSecondaryLightIsActuallyDark ? darkTile.icon : lightTile.icon,
          isSecondaryDarkIsActuallyDark ? darkTile.icon : lightTile.icon,
        ],
        tileTitle: [
          isSecondaryLightIsActuallyDark ? darkTile.title : lightTile.title,
          isSecondaryDarkIsActuallyDark ? darkTile.title : lightTile.title,
        ],
        tileText: [
          isSecondaryLightIsActuallyDark ? darkTile.text : lightTile.text,
          isSecondaryDarkIsActuallyDark ? darkTile.text : lightTile.text,
        ],
        search: [
          isSecondaryLightIsActuallyDark
            ? darkTile.background
            : lightTile.background,
          isSecondaryDarkIsActuallyDark
            ? darkTile.background
            : lightTile.background,
        ],
        searchHint: [
          isSecondaryLightIsActuallyDark ? darkTile.hint : lightTile.hint,
          isSecondaryDarkIsActuallyDark ? darkTile.hint : lightTile.hint,
        ],
        searchBorder: [
          isSecondaryLightIsActuallyDark ? darkTile.border : lightTile.border,
          isSecondaryDarkIsActuallyDark ? darkTile.border : lightTile.border,
        ],
        searchIcon: [
          isSecondaryLightIsActuallyDark ? searchDarkIcon : searchLightIcon,
          isSecondaryDarkIsActuallyDark ? searchDarkIcon : searchLightIcon,
        ],
        searchText: [
          isSecondaryLightIsActuallyDark ? darkTile.title : lightTile.title,
          isSecondaryDarkIsActuallyDark ? darkTile.title : lightTile.title,
        ],
        searchPlaceholder: [
          isSecondaryLightIsActuallyDark ? darkTile.text : lightTile.text,
          isSecondaryDarkIsActuallyDark ? darkTile.text : lightTile.text,
        ],
        link: [linkColorLight, linkColorDark],
        highlight: [highlightColorLight, highlightColorDark],
      },
      "hcColor"
    )[1].join("");
  }, [
    appSettings.primaryColorLight,
    appSettings.secondaryColorLight,
    appSettings.primaryColorDark,
    appSettings.secondaryColorDark,
  ]);

  const getUrlWithPreview = useUrlWithPreview();
  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);
  const router = useRouter();
  const isCustomerPortalPage = useMemo(
    () => router.pathname.includes(customerPortalPath),
    [router.pathname]
  );

  const pageTitleSuffix = `${
    appSettings.pageTitle || appSettings.title || appSettings.name
  }`;
  const normalizedPageTitle = isCustomPageTitle
    ? pageTitle
    : [pageTitle, pageTitleSuffix].filter(Boolean).join(" | ");

  const isArticle = appSettings.canonicalPageUrl.includes("/articles/");

  const ogImageContent = seoImageUrl || ssrLogoUrl;

  const headerStyle = useMemo(
    () =>
      (wallpaperUrl && { backgroundImage: `url(${wallpaperUrl})` }) ||
      undefined,
    [wallpaperUrl]
  );

  const logoContainerStyle = useMemo(
    () => (logoUrl && { backgroundImage: `url(${logoUrl})` }) || undefined,
    [logoUrl]
  );

  const isLogoUrlWhitelisted = useMemo<boolean>(() => {
    if (!logoUrl) return false;
    try {
      const url = new URL(logoUrl);
      return imageDomainsWhitelist.includes(url.host);
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [logoUrl]);

  return (
    <div
      className={`${styles.container} ${className || ""}`}
      itemScope
      itemType="http://schema.org/WebSite"
    >
      <meta
        itemProp="name"
        content={
          (pageTitle ?? appSettings.pageTitle) ||
          t("Help Center", appSettings.language)
        }
      />
      {ssrLogoUrl && (
        // While it is not displayed on the website it can be parsed by search engines to show site image
        <a href={baseUrl} itemProp="url" style={noDisplayStyle}>
          <Image
            src={ssrLogoUrl}
            data-original={ssrLogoUrl}
            itemProp="image"
            alt={
              (pageTitle ?? appSettings.pageTitle) ||
              t("Help Center", appSettings.language)
            }
            width={appSettings.logoWidth || 256}
            height={appSettings.logoHeight || 256}
          />
        </a>
      )}

      <Head>
        <title>{normalizedPageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link
          rel="canonical"
          href={canonicalUrl || appSettings.canonicalPageUrl}
        />

        {appSettings.faviconUrl ? (
          <link rel="icon" href={appSettings.faviconUrl} />
        ) : (
          <>
            <link
              rel="apple-touch-icon"
              sizes="180x180"
              href="/apple-touch-icon.png"
            />
            <link rel="icon" href="/favicon.ico" />
            <link
              rel="icon"
              type="image/png"
              sizes="32x32"
              href="/favicon-32x32.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href="/favicon-16x16.png"
            />
          </>
        )}
        <meta name="theme-color" content="#ffffff" />

        {appSettings.hcGoogleSiteVerificationCode && (
          <meta
            name="google-site-verification"
            content={appSettings.hcGoogleSiteVerificationCode}
          />
        )}

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {ogImageContent && (
          <meta property="og:image" content={ogImageContent} />
        )}
        {appSettings.pageTitle ? (
          <meta property="og:site_name" content={appSettings.pageTitle} />
        ) : null}
        <meta property="og:url" content={appSettings.canonicalPageUrl} />
        {isArticle && <meta property="og:type" content="article" />}
        {articleLocale && <meta property="og:locale" content={articleLocale} />}

        {/* "html body" is needed to overwrite styles from StylesProvider */}
        <style>{`
          html body {
            background-color: var(--hcColor-secondary);
          }
        `}</style>
      </Head>

      <style suppressHydrationWarning>{colorsCss}</style>

      <AtlasSnippet
        appId={appSettings.appId}
        chatting={appSettings.chatting}
        enableChattingForVisitors={appSettings.enableChattingForVisitors}
        recording={appSettings.sessionRecording}
        baseUrl={baseUrl}
      />

      <header
        className={cn(styles.header, { [styles.withSearch]: !hideSearch })}
        style={headerStyle}
      >
        <div className={`${styles.content} ${styles.headerContent}`}>
          <Link
            href={getUrlWithPreview(
              baseUrl.endsWith("/") ? baseUrl : baseUrl + "/"
            )}
            className={styles.headerTitleLink}
          >
            {logoUrl && (
              <span
                className={styles.headerTitleLinkLogoContainer}
                style={logoContainerStyle}
              >
                {isLogoUrlWhitelisted ? (
                  <Image
                    className={styles.headerLogo}
                    src={logoUrl}
                    alt={appSettings.name}
                    width={800}
                    height={80}
                  />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={logoUrl} alt={appSettings.name} />
                )}
              </span>
            )}
            {hasTitle ? (
              <h1 className={styles.headerTitle}>{appSettings.title}</h1>
            ) : (
              <span className={styles.headerTitle}>{appSettings.title}</span>
            )}
          </Link>
          {appSettings.enableSupportPortal && (
            <Link
              href={getUrlWithPreview(
                `${baseUrl}${isCustomerPortalPage ? "" : customerPortalPath}`
              )}
              className={styles.headerItem}
            >
              {isCustomerPortalPage
                ? t("Help Center", appSettings.language)
                : appSettings.supportPortalTitle}
            </Link>
          )}
          {appSettings.links?.map((link) => (
            <Link
              key={link.id}
              href={getUrlWithPreview(link.url)}
              target="_blank"
              className={styles.headerItem}
            >
              <span className={styles.headerItemLabel}>{link.label}</span>
              {link.icon && (
                <big className={styles.headerLinkIcon}>
                  {link.icon.match(/https?:\/\//i) ? (
                    <img
                      src={link.icon}
                      alt="icon"
                      className={styles.headerLinkIconImage}
                    />
                  ) : (
                    link.icon
                  )}
                </big>
              )}
            </Link>
          ))}
          {/* {customer && !customer.isVisitor && (
            <UserDetails className={styles.headerItem} customer={customer} />
          )} */}
          <div className={styles.headerSearch}>
            {!hideSearch && (
              <SearchBar
                appSettings={appSettings}
                baseUrl={baseUrl}
                className={styles.headerSearchBar}
                title={t("What can we help you find?", appSettings.language)}
                defaultValue={searchValue}
              />
            )}
            {hideSearch && header ? header : undefined}
          </div>
        </div>
      </header>
      <main
        className={cn(styles.main, {
          [styles.withoutTopPadding]: noContentTopPadding,
        })}
      >
        {top ? <div className={styles.content}>{top}</div> : null}
        <div className={styles.content}>
          {sidebar ? (
            <div className={styles.layoutColumns}>
              <div className={styles.layoutSidebarColumn}>{sidebar}</div>
              <div className={styles.layoutContentColumn}>{children}</div>
            </div>
          ) : (
            children
          )}
        </div>
      </main>

      {!hideFooter && (
        <div className={cn(styles.content, styles.pushTop)}>
          <LayoutFooter
            className={cn(styles.layoutFooter, {
              [styles.layoutContentColumn]: sidebar,
            })}
            appSettings={appSettings}
          />
        </div>
      )}
    </div>
  );
}

export const minTwoColumnScreenWidth = 1100;

const noDisplayStyle: React.CSSProperties = { display: "none" };

// function UserDetails({
//   className,
//   customer,
// }: {
//   className?: string;
//   customer: Customer;
// }) {
//   return (
//     <span className={className}>
//       {customer.fullName}
//       &nbsp;&nbsp;
//       {customer.fields?.photo && <UserAvatar user={customer} />}
//     </span>
//   );
// }
